import { autocompletion, Completion, CompletionContext } from '@codemirror/autocomplete'
import { ArrowDropDownRounded, ArrowDropUpRounded, DatasetRounded, FilterAltOffRounded, FilterAltRounded, NotListedLocationRounded } from '@mui/icons-material'
import { Badge, Box, Button, Collapse, IconButton, MenuItem, Popover, Stack, TextField, Tooltip, Typography } from '@mui/material'
import { ChangeEvent, memo, useEffect, useMemo, useState } from 'react'
import { INodeReportPlaceholder } from '../../../../../definitions/Nodes'
import NodeCodemirrorManager from '../../../../../managers/NodeCodemirrorManager'
import { IShapeInput } from '../../../../../managers/NodeManager'
import NodeCodemirrorTextEditor from '../../NodeCodemirrorTextEditor'
import ColumnPlaceholder from './ColumnPlaceholder'
import { useDebouncedCommit } from './NodeReportEditor'

interface P {
    contents: { name: string, contents: string[] }[]
    placeholder: INodeReportPlaceholder
    placeholderIdx: number
    disable: boolean
    onDatasetPlaceholderChange: (newValue: string, placeholderIdx: number) => void
    onDatasetPlaceholderConditionClear: (placeholderIdx: number) => void
    onDatasetPlaceholderConditionChange: (placeholderIdx: number, keyInput: string, operatorInput: '=' | '!=' | '>' | '<' | '>=' | '<=' | 'includes', valueInput: string) => void
    onColumnPlaceholderChange: (placeholderIdx: number, columnIdx: number, val: string) => void
    shapes: IShapeInput[]
    globalsSuggestions: Completion[]
}

const comparisonOperations = ['=', '!=', '>', '<', '>=', '<=', 'includes']

const DatasetPlaceholder = ({ contents, placeholder, placeholderIdx, disable, onDatasetPlaceholderChange, onDatasetPlaceholderConditionClear, onDatasetPlaceholderConditionChange, onColumnPlaceholderChange: onColumnPlaceholderConditionChange, shapes, globalsSuggestions }: P) => {
    const [openCollapse, setOpenCollapse] = useState(false)
    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)
    const [conditionKeyInput, setConditionKeyInput] = useState(placeholder?.condition?.key ?? '')
    const [conditionOperatorInput, setConditionOperatorInput] = useState<'=' | '!=' | '>' | '<' | '>=' | '<=' | 'includes'>(placeholder?.condition?.operator ?? '=')
    const [conditionValueInput, setConditionValueInput] = useState(placeholder?.condition?.value ?? '')

    const open = Boolean(anchorEl)

    const keywords = NodeCodemirrorManager.generateCompletionsFromShapes(shapes)
    const placeholderContents = contents?.find(c => c.name === placeholder.name)?.contents

    useEffect(() => {
        setConditionKeyInput(placeholder?.condition?.key ?? '')
        setConditionOperatorInput(placeholder?.condition?.operator ?? '=')
        setConditionValueInput(placeholder?.condition?.value ?? '')
    }, [placeholder?.condition])

    const commitPlaceholder = (val: string) => {
        const parsedValue = `@{${NodeCodemirrorManager.changeLabelToUuid(val, keywords)}}`
        onDatasetPlaceholderChange(parsedValue, placeholderIdx)
    }

    const [input, setInput] = useDebouncedCommit(
        placeholder.referenceData
            ? NodeCodemirrorManager.changeLabelToUuid(placeholder.referenceData.slice(2, -1), keywords, true)
            : '',
        commitPlaceholder,
        500
    )

    const customCompletions = (context: CompletionContext) => {
        const word = context.matchBefore(/\w*/)
        if (word.from === word.to && !context.explicit) return null
        return { from: word.from, options: keywords }
    }

    const globalsCompletions = (context: CompletionContext) => {
        const word = context.matchBefore(/\w*/)
        if (word.from === word.to && !context.explicit) return null
        return { from: word.from, options: globalsSuggestions }
    }

    const shape = useMemo(() => {
        const parts = placeholder.referenceData.slice(2, -1).split('.')
        const foundShape = shapes.find(shape => shape.nodeUuid === parts[0])
        const shapeObj = foundShape?.shape?.items?.properties ?? foundShape?.shape?.properties ?? {}
        return Object.keys(shapeObj)
    }, [placeholder, shapes])

    return (
        <Stack spacing={1}>
            <Stack flexDirection='row' alignItems='center' gap={1}>
                <Stack flexDirection='row' alignItems='center' width={170} gap={1} overflow='hidden'>
                    <IconButton size='small' onClick={() => setOpenCollapse(prev => !prev)}>
                        {openCollapse ? <ArrowDropUpRounded fontSize='small' /> : <ArrowDropDownRounded fontSize='small' />}
                    </IconButton>
                    <DatasetRounded fontSize='small' />
                    <Typography noWrap={!openCollapse} variant='subtitle2'>{placeholder.name}</Typography>
                </Stack>
                <Stack flexDirection='row' flex={1} overflow='hidden'>
                    <NodeCodemirrorTextEditor
                        disabled={disable}
                        value={input}
                        onChange={setInput}
                        extensions={[autocompletion({ override: [customCompletions] })]}
                    />
                </Stack>
                <IconButton size='small' onClick={e => setAnchorEl(e.currentTarget)}>
                    <Badge
                        variant='dot'
                        color='secondary'
                        invisible={!(conditionKeyInput && conditionValueInput)}>
                        <FilterAltRounded fontSize='small' />
                    </Badge>
                </IconButton>

                {/* Filter condition */}
                <Popover
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                    transformOrigin={{ horizontal: 500, vertical: 0 }}
                    open={open}
                    anchorEl={anchorEl}
                    onClose={() => setAnchorEl(null)}>
                    <Stack padding={2} width={500} spacing={1} sx={{ backgroundColor: theme => theme.palette.background.smartable }}>
                        <Stack flexDirection='row' gap={1} alignItems='center'>
                            <TextField
                                disabled={disable}
                                placeholder='key'
                                fullWidth
                                size='small'
                                value={conditionKeyInput}
                                onChange={(e: ChangeEvent<HTMLInputElement>) => setConditionKeyInput(e.target.value)}
                            />
                            <TextField
                                disabled={disable}
                                placeholder='operator'
                                fullWidth
                                size='small'
                                select
                                value={conditionOperatorInput}
                                onChange={(e: ChangeEvent<HTMLInputElement>) => setConditionOperatorInput(e.target.value as | '=' | '!=' | '>' | '<' | '>=' | '<=' | 'includes')}>
                                {comparisonOperations.map(op => (
                                    <MenuItem key={op} value={op}>
                                        {op.toUpperCase()}
                                    </MenuItem>
                                ))}
                            </TextField>
                            <NodeCodemirrorTextEditor
                                disabled={disable}
                                placeholder='value'
                                style={{ overflow: 'hidden', minWidth: '100%' }}
                                value={conditionValueInput}
                                onChange={val => setConditionValueInput(val)}
                                extensions={[autocompletion({ override: [globalsCompletions] })]}
                            />
                            <IconButton
                                disabled={disable}
                                size='small'
                                onClick={() => {
                                    setConditionKeyInput('')
                                    setConditionOperatorInput('=')
                                    setConditionValueInput('')
                                    onDatasetPlaceholderConditionClear(placeholderIdx)
                                }}>
                                <FilterAltOffRounded fontSize='small' />
                            </IconButton>
                        </Stack>
                        <Button
                            disabled={disable || !conditionKeyInput || !conditionValueInput}
                            color='secondary'
                            size='small'
                            variant='text'
                            onClick={() => {
                                onDatasetPlaceholderConditionChange(placeholderIdx, conditionKeyInput, conditionOperatorInput, conditionValueInput)
                                setAnchorEl(null)
                            }}>
                            Apply filter
                        </Button>
                    </Stack>
                </Popover>

                <Tooltip
                    title={
                        placeholderContents?.length > 0
                            ? placeholderContents.map((c, i) => <Box key={i}>{c}</Box>)
                            : ''
                    }
                    placement='left'
                    arrow>
                    <NotListedLocationRounded
                        fontSize='small'
                        color={placeholderContents?.length === 0 ? 'disabled' : undefined}
                    />
                </Tooltip>
            </Stack>

            {/* Column placeholders */}
            <Collapse in={openCollapse} timeout='auto' unmountOnExit>
                <Stack spacing={1} paddingBottom={openCollapse ? 3 : undefined}>
                    {placeholder.columns.map((column, columnIdx) => (
                        <MemoColumnPlaceholder
                            key={`${column.name}-${placeholderIdx}-${columnIdx}-column`}
                            column={column}
                            columnIdx={columnIdx}
                            placeholderIdx={placeholderIdx}
                            disable={disable}
                            onColumnPlaceholderChange={onColumnPlaceholderConditionChange}
                            shape={shape}
                            globalsSuggestions={globalsSuggestions}
                        />
                    ))}
                </Stack>
            </Collapse>
        </Stack>
    )
}

const MemoColumnPlaceholder = memo(ColumnPlaceholder, (prev, next) => {
    return (
        prev.column === next.column
    )
})

export default memo(DatasetPlaceholder)
